<script>
	import { onMount } from 'svelte';
	import { currentUser, dashboardStats } from './store.js';
	import { statsAPI, agentsAPI, billingAPI } from './api.js';
	import Icon from './lib/Icon.svelte';
	import OnboardingCard from './lib/OnboardingCard.svelte';

	let { onNavigate = (_view) => {} } = $props();

	let loading = $state(true);
	let error = $state('');
	let series = $state([]);
	let funnel = $state([]);
	let convStatus = $state({ open: 0, pending: 0, resolved: 0, closed: 0 });
	let leadStats = $state({ active: 0, won: 0, lost: 0, pipeline_value: 0, won_value: 0 });
	let agentStats = $state(null);
	let balance = $state(null);
	let rangeDays = $state(14);

	async function load() {
		loading = true;
		error = '';
		try {
			const data = await statsAPI.getSeries(rangeDays);
			series = data.series || [];
			funnel = data.funnel || [];
			convStatus = data.conversation_status || convStatus;
			leadStats = data.leads || leadStats;
		} catch (e) {
			error = e.message || 'Error cargando estadísticas';
		} finally {
			loading = false;
		}
		// Secondary loads: tolerate individual failures silently
		try { agentStats = await agentsAPI.statsSeries(rangeDays); } catch {}
		try { balance = await billingAPI.balance(); } catch {}
	}
	onMount(load);

	// Show the credits card only when relevant (has balance or platform usage)
	let showBalanceCard = $derived(
		balance && (balance.balance_usd > 0 || balance.consumed_this_month_usd > 0 || balance.low_balance)
	);

	function fmtUsd(v) {
		return `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
	}

	function fmtMoney(v, currency = 'USD') {
		const n = Number(v) || 0;
		try {
			return new Intl.NumberFormat('en-US', {
				style: 'currency', currency, maximumFractionDigits: 0
			}).format(n);
		} catch { return `$${n.toFixed(0)}`; }
	}

	// ----- chart geometry -----
	const W = 560;
	const H = 180;
	const PADX = 32;
	const PADY = 18;

	let maxConv = $derived(Math.max(1, ...series.map(s => s.conversations)));
	let maxMsg  = $derived(Math.max(1, ...series.map(s => s.messages)));
	let maxLead = $derived(Math.max(1, ...series.map(s => s.leads)));

	function pointsFor(key, max) {
		if (!series.length) return '';
		const stepX = (W - PADX * 2) / Math.max(1, series.length - 1);
		return series.map((p, i) => {
			const x = PADX + i * stepX;
			const y = H - PADY - ((p[key] / max) * (H - PADY * 2));
			return `${x.toFixed(1)},${y.toFixed(1)}`;
		}).join(' ');
	}
	function areaFor(key, max) {
		const pts = pointsFor(key, max);
		if (!pts) return '';
		return `M ${PADX},${H - PADY} L ${pts.replaceAll(' ', ' L ')} L ${W - PADX},${H - PADY} Z`;
	}

	const STATUS_LABELS = { open: 'Abiertas', pending: 'Pendientes', resolved: 'Resueltas', closed: 'Cerradas' };

	// donut for conversation status
	let donut = $derived.by(() => {
		const slices = [
			{ key: 'open',     color: 'var(--info)',    value: convStatus.open || 0 },
			{ key: 'pending',  color: 'var(--warning)', value: convStatus.pending || 0 },
			{ key: 'resolved', color: 'var(--success)', value: convStatus.resolved || 0 },
			{ key: 'closed',   color: 'var(--text-3)',  value: convStatus.closed || 0 }
		];
		const total = slices.reduce((s, x) => s + x.value, 0);
		let acc = 0;
		const C = 2 * Math.PI * 36;
		const arcs = slices.map(s => {
			const len = total ? (s.value / total) * C : 0;
			const arc = { ...s, label: STATUS_LABELS[s.key], dasharray: `${len} ${C - len}`, offset: -acc };
			acc += len;
			return arc;
		});
		return { slices: arcs, total };
	});

	let maxFunnel = $derived(Math.max(1, ...funnel.map(s => s.count)));
	let totalConvs = $derived(series.reduce((sum, s) => sum + s.conversations, 0));
	let totalMessages = $derived(series.reduce((sum, s) => sum + s.messages, 0));
	let totalNewLeads = $derived(series.reduce((sum, s) => sum + s.leads, 0));

	function fmtDay(iso) {
		try {
			const d = new Date(iso);
			return d.toLocaleDateString('es', { day: '2-digit', month: 'short' });
		} catch { return iso; }
	}
</script>

<div class="home">
	<header class="hero">
		<div>
			<h2>Hola, {$currentUser?.name?.split(' ')[0] ?? ''} 👋</h2>
			<p class="muted">Resumen de los últimos {rangeDays} días.</p>
		</div>
		<div class="range-tabs" role="tablist" aria-label="Rango">
			{#each [7, 14, 30] as d}
				<button
					role="tab"
					class:active={rangeDays === d}
					onclick={() => { rangeDays = d; load(); }}
				>{d}d</button>
			{/each}
		</div>
	</header>

	<OnboardingCard {onNavigate} />

	{#if error}
		<div class="card error-card">
			<Icon name="alert-circle" size={16} />
			<span>{error}</span>
		</div>
	{/if}

	<!-- KPIs -->
	<section class="kpi-grid">
		<div class="kpi card">
			<div class="kpi-icon brand"><Icon name="message-square" size={18} /></div>
			<div>
				<div class="kpi-value">{totalConvs}</div>
				<div class="kpi-label">Nuevas conversaciones</div>
			</div>
		</div>
		<div class="kpi card">
			<div class="kpi-icon accent"><Icon name="inbox" size={18} /></div>
			<div>
				<div class="kpi-value">{totalMessages}</div>
				<div class="kpi-label">Mensajes recibidos</div>
			</div>
		</div>
		<div class="kpi card">
			<div class="kpi-icon info"><Icon name="users" size={18} /></div>
			<div>
				<div class="kpi-value">{totalNewLeads}</div>
				<div class="kpi-label">Nuevos leads</div>
			</div>
		</div>
		<div class="kpi card">
			<div class="kpi-icon success"><Icon name="trending-up" size={18} /></div>
			<div>
				<div class="kpi-value">{fmtMoney(leadStats.pipeline_value)}</div>
				<div class="kpi-label">Valor en pipeline</div>
			</div>
		</div>
	</section>

	<!-- AI agents KPIs + credit balance -->
	{#if agentStats?.totals?.interactions > 0 || showBalanceCard}
		<section class="kpi-grid">
			<div class="kpi card">
				<div class="kpi-icon accent"><Icon name="sparkles" size={18} /></div>
				<div>
					<div class="kpi-value">{agentStats?.totals?.interactions ?? 0}</div>
					<div class="kpi-label">Respuestas de IA</div>
				</div>
			</div>
			<div class="kpi card">
				<div class="kpi-icon success"><Icon name="heart" size={18} /></div>
				<div>
					<div class="kpi-value">{agentStats?.totals?.satisfaction != null ? `${agentStats.totals.satisfaction}%` : '—'}</div>
					<div class="kpi-label">Satisfacción</div>
				</div>
			</div>
			<div class="kpi card">
				<div class="kpi-icon info"><Icon name="users" size={18} /></div>
				<div>
					<div class="kpi-value">{agentStats?.totals?.handed_off ?? 0}</div>
					<div class="kpi-label">Handoffs a humanos</div>
				</div>
			</div>
			{#if showBalanceCard}
				<button class="kpi card balance-card" class:low={balance.low_balance} onclick={() => onNavigate('settings')} type="button">
					<div class="kpi-icon {balance.low_balance ? 'warn' : 'brand'}"><Icon name="zap" size={18} /></div>
					<div>
						<div class="kpi-value">₡{Math.round(balance.balance_crc).toLocaleString('es-CR')}</div>
						<div class="kpi-label">
							Saldo de IA · {fmtUsd(balance.balance_usd)}
							{#if balance.low_balance}<span class="low-tag">Saldo bajo</span>{/if}
						</div>
					</div>
				</button>
			{:else}
				<div class="kpi card">
					<div class="kpi-icon warn"><Icon name="zap" size={18} /></div>
					<div>
						<div class="kpi-value">{(agentStats?.totals?.tokens ?? 0).toLocaleString()}</div>
						<div class="kpi-label">Tokens consumidos</div>
					</div>
				</div>
			{/if}
		</section>
	{/if}

	<!-- Charts row -->
	<section class="charts">
		<!-- Activity area chart -->
		<div class="card chart-card">
			<div class="card-head">
				<div>
					<h3>Actividad</h3>
					<p class="muted">Conversaciones, mensajes y leads por día</p>
				</div>
				<div class="legend">
					<span class="leg brand"></span> Conversaciones
					<span class="leg accent" style="margin-left:12px"></span> Mensajes
					<span class="leg success" style="margin-left:12px"></span> Leads
				</div>
			</div>
			{#if loading}
				<div class="chart-skeleton skeleton"></div>
			{:else if !series.length}
				<div class="chart-empty">No hay datos en este período</div>
			{:else}
				<svg viewBox="0 0 {W} {H}" class="chart" role="img" aria-label="Actividad">
					<!-- grid -->
					{#each [0.25, 0.5, 0.75, 1] as p}
						<line x1={PADX} x2={W - PADX} y1={H - PADY - p * (H - PADY * 2)} y2={H - PADY - p * (H - PADY * 2)} stroke="var(--border-1)" stroke-dasharray="2 4" />
					{/each}
					<!-- areas -->
					<path d={areaFor('messages', maxMsg)} fill="var(--accent)" opacity="0.08" />
					<path d={areaFor('conversations', maxConv)} fill="var(--brand)" opacity="0.10" />
					<!-- lines -->
					<polyline fill="none" stroke="var(--accent)" stroke-width="2" points={pointsFor('messages', maxMsg)} />
					<polyline fill="none" stroke="var(--brand)" stroke-width="2" points={pointsFor('conversations', maxConv)} />
					<polyline fill="none" stroke="var(--success)" stroke-width="2" stroke-dasharray="3 3" points={pointsFor('leads', maxLead)} />
					<!-- points -->
					{#each series as p, i}
						{@const stepX = (W - PADX * 2) / Math.max(1, series.length - 1)}
						{@const x = PADX + i * stepX}
						<circle cx={x} cy={H - PADY - (p.conversations / maxConv) * (H - PADY * 2)} r="2.5" fill="var(--brand)" />
					{/each}
				</svg>
				<div class="x-axis">
					{#each series as p, i}
						{#if i % Math.ceil(series.length / 7) === 0}
							<span>{fmtDay(p.date)}</span>
						{/if}
					{/each}
				</div>
			{/if}
		</div>

		<!-- Conversation status donut -->
		<div class="card donut-card">
			<div class="card-head"><h3>Estado de conversaciones</h3></div>
			{#if loading}
				<div class="chart-skeleton skeleton"></div>
			{:else if donut.total === 0}
				<div class="chart-empty">Sin conversaciones todavía</div>
			{:else}
				<div class="donut-wrap">
					<svg viewBox="0 0 100 100" class="donut" role="img" aria-label="Estado conversaciones">
						<circle cx="50" cy="50" r="36" fill="none" stroke="var(--surface-2)" stroke-width="14" />
						{#each donut.slices as s}
							{#if s.value > 0}
								<circle
									cx="50" cy="50" r="36"
									fill="none"
									stroke={s.color}
									stroke-width="14"
									stroke-dasharray={s.dasharray}
									stroke-dashoffset={s.offset}
									transform="rotate(-90 50 50)"
								/>
							{/if}
						{/each}
						<text x="50" y="48" text-anchor="middle" font-size="14" font-weight="700" fill="var(--text-1)">
							{donut.total}
						</text>
						<text x="50" y="60" text-anchor="middle" font-size="6" fill="var(--text-3)">
							TOTAL
						</text>
					</svg>
					<ul class="donut-legend">
						{#each donut.slices as s}
							<li>
								<span class="dot" style="background:{s.color}"></span>
								<span class="lbl">{s.label}</span>
								<span class="val">{s.value}</span>
							</li>
						{/each}
					</ul>
				</div>
			{/if}
		</div>
	</section>

	<!-- Funnel + activity -->
	<section class="charts">
		<div class="card funnel-card">
			<div class="card-head">
				<div>
					<h3>Embudo de leads</h3>
					<p class="muted">Distribución actual por etapa</p>
				</div>
				<button class="btn btn-ghost btn-sm" onclick={() => onNavigate('leads')}>
					Ver pipeline
					<Icon name="arrow-right" size={14} />
				</button>
			</div>
			{#if loading}
				<div class="chart-skeleton skeleton"></div>
			{:else if !funnel.length}
				<div class="chart-empty">Crea tu primer pipeline en "Leads"</div>
			{:else}
				<ul class="funnel-list">
					{#each funnel as s}
						{@const pct = Math.round((s.count / maxFunnel) * 100)}
						<li>
							<div class="f-row">
								<span class="dot" style="background:{s.color}"></span>
								<span class="f-name">{s.name}</span>
								<span class="f-meta">{s.count} {s.count === 1 ? 'lead' : 'leads'} · {fmtMoney(s.value)}</span>
							</div>
							<div class="f-bar">
								<div class="f-bar-fill" style="width:{pct}%; background:{s.color}"></div>
							</div>
						</li>
					{/each}
				</ul>
			{/if}
		</div>

		<div class="card lead-summary-card">
			<div class="card-head"><h3>Leads</h3></div>
			<ul class="ls-list">
				<li>
					<div class="ls-icon" style="background:var(--accent-soft); color:var(--accent)">
						<Icon name="kanban" size={16} />
					</div>
					<div class="ls-text">
						<div class="ls-label">Activos</div>
						<div class="ls-meta">{leadStats.active} · {fmtMoney(leadStats.pipeline_value)}</div>
					</div>
				</li>
				<li>
					<div class="ls-icon" style="background:var(--success-soft); color:var(--success)">
						<Icon name="check-circle" size={16} />
					</div>
					<div class="ls-text">
						<div class="ls-label">Ganados</div>
						<div class="ls-meta">{leadStats.won} · {fmtMoney(leadStats.won_value)}</div>
					</div>
				</li>
				<li>
					<div class="ls-icon" style="background:var(--danger-soft); color:var(--danger)">
						<Icon name="x" size={16} />
					</div>
					<div class="ls-text">
						<div class="ls-label">Perdidos</div>
						<div class="ls-meta">{leadStats.lost}</div>
					</div>
				</li>
			</ul>
			<button class="btn btn-secondary btn-block-cta" onclick={() => onNavigate('leads')}>
				<Icon name="plus" size={14} /> Crear lead
			</button>
		</div>
	</section>

	<!-- Top agentes IA -->
	{#if agentStats?.top_agents?.length}
		<section class="card" style="margin-top: 18px; padding: 18px;">
			<div class="card-head">
				<div>
					<h3>Agentes IA</h3>
					<p class="muted">Rendimiento de los últimos {agentStats.days} días</p>
				</div>
				<button class="btn btn-ghost btn-sm" onclick={() => onNavigate('ai-agents')}>
					Ver agentes <Icon name="arrow-right" size={13} />
				</button>
			</div>
			<table class="agents-table">
				<thead>
					<tr><th>Agente</th><th>Respuestas</th><th>Satisfacción</th><th>Tokens</th>{#if balance}<th>Costo</th>{/if}</tr>
				</thead>
				<tbody>
					{#each agentStats.top_agents as a (a.id)}
						<tr>
							<td><b>{a.name}</b></td>
							<td>{a.interactions}</td>
							<td>{a.satisfaction != null ? `${a.satisfaction}%` : '—'}</td>
							<td>{a.tokens.toLocaleString()}</td>
							{#if balance}<td>{a.cost_usd > 0 ? fmtUsd(a.cost_usd) : '—'}</td>{/if}
						</tr>
					{/each}
				</tbody>
			</table>
		</section>
	{/if}
</div>

<style>
	.home {
		padding: 24px 28px 32px;
		max-width: 1280px;
		margin: 0 auto;
	}

	.hero {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 16px;
		margin-bottom: 20px;
	}
	.hero h2 { font-size: 20px; margin-bottom: 4px; }
	.muted { color: var(--text-3); font-size: 13px; margin: 0; }

	.range-tabs {
		display: flex;
		background: var(--surface-2);
		border-radius: 8px;
		padding: 3px;
		gap: 2px;
	}
	.range-tabs button {
		padding: 5px 12px;
		font-size: 12px;
		font-weight: 600;
		color: var(--text-3);
		border-radius: 5px;
		transition: background var(--transition-fast), color var(--transition-fast);
	}
	.range-tabs button:hover { color: var(--text-1); }
	.range-tabs button.active { background: var(--surface-0); color: var(--text-1); box-shadow: var(--shadow-xs); }

	.error-card {
		display: flex; align-items: center; gap: 8px;
		color: var(--danger);
		background: var(--danger-soft);
		border-color: var(--danger-soft);
		padding: 10px 14px;
		font-size: 13px;
		margin-bottom: 16px;
	}

	/* KPI grid */
	.kpi-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
		gap: 14px;
		margin-bottom: 16px;
	}
	.kpi {
		padding: 16px;
		display: flex; align-items: center; gap: 12px;
	}
	.kpi-icon {
		width: 40px; height: 40px;
		border-radius: 10px;
		display: flex; align-items: center; justify-content: center;
		flex-shrink: 0;
	}
	.kpi-icon.brand   { background: var(--brand-soft);   color: var(--brand-dark); }
	.kpi-icon.accent  { background: var(--accent-soft);  color: var(--accent); }
	.kpi-icon.info    { background: var(--info-soft);    color: var(--info); }
	.kpi-icon.success { background: var(--success-soft); color: var(--success); }
	.kpi-icon.warn    { background: var(--warning-soft); color: #b45309; }

	/* Saldo de IA */
	.balance-card { text-align: left; cursor: pointer; transition: box-shadow var(--transition-fast); }
	.balance-card:hover { box-shadow: var(--shadow-md); }
	.balance-card.low { border-color: var(--warning); background: var(--warning-soft); }
	.low-tag {
		display: inline-block;
		margin-left: 6px;
		padding: 1px 7px;
		border-radius: var(--radius-full);
		background: var(--danger);
		color: var(--text-inverse);
		font-size: 10px;
		font-weight: 700;
	}

	/* Top agentes */
	.agents-table { width: 100%; border-collapse: collapse; font-size: 13px; }
	.agents-table th {
		text-align: left; padding: 8px 10px;
		font-size: 11px; font-weight: 700; color: var(--text-3);
		text-transform: uppercase; letter-spacing: 0.04em;
		border-bottom: 1px solid var(--border-1);
	}
	.agents-table td { padding: 9px 10px; border-bottom: 1px solid var(--border-1); }
	.agents-table tr:last-child td { border-bottom: none; }
	.kpi-value { font-size: 20px; font-weight: 700; line-height: 1.1; }
	.kpi-label { font-size: 12px; color: var(--text-3); margin-top: 2px; }

	/* Charts */
	.charts {
		display: grid;
		grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
		gap: 14px;
		margin-bottom: 14px;
	}
	@media (max-width: 880px) {
		.charts { grid-template-columns: 1fr; }
	}

	.chart-card, .donut-card, .funnel-card, .lead-summary-card {
		padding: 16px 18px;
		min-height: 240px;
		display: flex; flex-direction: column;
	}
	.card-head {
		display: flex; align-items: flex-start; justify-content: space-between;
		gap: 10px;
		margin-bottom: 12px;
	}
	.card-head h3 { font-size: 14px; font-weight: 600; margin-bottom: 2px; }

	.legend {
		font-size: 11px;
		color: var(--text-3);
		display: flex; align-items: center;
		flex-wrap: wrap;
	}
	.leg {
		display: inline-block;
		width: 8px; height: 8px;
		border-radius: 50%;
		margin-right: 5px;
	}
	.leg.brand { background: var(--brand); }
	.leg.accent { background: var(--accent); }
	.leg.success { background: var(--success); }

	.chart {
		width: 100%;
		height: auto;
		max-height: 220px;
	}
	.chart-skeleton {
		flex: 1;
		min-height: 180px;
	}
	.chart-empty {
		flex: 1;
		display: flex; align-items: center; justify-content: center;
		color: var(--text-3);
		font-size: 13px;
	}
	.x-axis {
		display: flex;
		justify-content: space-between;
		padding: 0 32px;
		font-size: 10px;
		color: var(--text-3);
		margin-top: 4px;
	}

	.donut-wrap {
		display: flex; align-items: center; gap: 16px;
		flex: 1;
	}
	.donut { width: 130px; height: 130px; flex-shrink: 0; }
	.donut-legend {
		list-style: none; margin: 0; padding: 0;
		display: flex; flex-direction: column; gap: 6px;
		flex: 1;
	}
	.donut-legend li {
		display: flex; align-items: center; gap: 8px;
		font-size: 12.5px;
	}
	.donut-legend .lbl { color: var(--text-2); flex: 1; }
	.donut-legend .val { font-weight: 600; color: var(--text-1); }
	.dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

	/* Funnel */
	.funnel-list {
		list-style: none; margin: 0; padding: 0;
		display: flex; flex-direction: column;
		gap: 10px;
		flex: 1;
	}
	.funnel-list li { display: flex; flex-direction: column; gap: 4px; }
	.f-row {
		display: flex; align-items: center; gap: 8px;
		font-size: 12.5px;
	}
	.f-name { font-weight: 600; color: var(--text-1); flex: 1; }
	.f-meta { color: var(--text-3); }
	.f-bar {
		height: 8px;
		background: var(--surface-2);
		border-radius: 4px;
		overflow: hidden;
	}
	.f-bar-fill {
		height: 100%;
		border-radius: 4px;
		transition: width 600ms cubic-bezier(0.4, 0, 0.2, 1);
	}

	/* Lead summary card */
	.ls-list {
		list-style: none; margin: 0 0 12px; padding: 0;
		display: flex; flex-direction: column; gap: 8px;
		flex: 1;
	}
	.ls-list li { display: flex; align-items: center; gap: 10px; }
	.ls-icon {
		width: 32px; height: 32px;
		border-radius: 8px;
		display: flex; align-items: center; justify-content: center;
		flex-shrink: 0;
	}
	.ls-text { flex: 1; }
	.ls-label { font-size: 12.5px; font-weight: 600; color: var(--text-1); }
	.ls-meta { font-size: 11.5px; color: var(--text-3); }
	.btn-block-cta {
		justify-content: center;
		width: 100%;
		padding: 9px 12px;
		font-size: 13px;
	}
</style>
